/**
 * Validation Engine
 * ─────────────────
 * Evaluates the active draft of parameter values against built-in type rules
 * and admin-authored business rules (cross-field date ranges, min/max, required,
 * regex, allowed values). Returns Apple-style inline errors keyed by parameter.
 *
 * Pure and synchronous: the UI calls this on every change to drive inline
 * feedback and to gate the Go button.
 */

import type {
  ParameterDescriptor,
  ParameterValue,
  ValidationError,
  ValidationRule,
} from "@/types";

export interface ValidationInput {
  descriptors: ParameterDescriptor[];
  /** Current draft values keyed by parameterId. */
  values: Record<string, ParameterValue>;
  rules: ValidationRule[];
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  /** Errors grouped by parameterId for convenient inline rendering. */
  byParameter: Record<string, ValidationError[]>;
}

function isBlank(value: ParameterValue): boolean {
  return value === null || value === undefined || value === "";
}

function toNumber(value: ParameterValue): number | null {
  if (value === null || value === "") return null;
  const n = typeof value === "boolean" ? (value ? 1 : 0) : Number(value);
  return Number.isFinite(n) ? n : null;
}

function toTime(value: ParameterValue): number | null {
  if (isBlank(value)) return null;
  const t = new Date(value as string).getTime();
  return Number.isNaN(t) ? null : t;
}

/** Built-in, type-driven validation (no admin rule needed). */
function validateTypes(
  descriptors: ParameterDescriptor[],
  values: Record<string, ParameterValue>,
): ValidationError[] {
  const errors: ValidationError[] = [];
  for (const d of descriptors) {
    const value = values[d.id];

    if (d.required && isBlank(value)) {
      errors.push({
        parameterId: d.id,
        ruleId: "builtin-required",
        message: `${d.name} is required.`,
      });
      continue;
    }
    if (isBlank(value)) continue;

    if (d.dataType === "int" || d.dataType === "float") {
      const n = toNumber(value);
      if (n === null) {
        errors.push({
          parameterId: d.id,
          ruleId: "builtin-number",
          message: `${d.name} must be a valid number.`,
        });
        continue;
      }
      if (d.dataType === "int" && !Number.isInteger(n)) {
        errors.push({
          parameterId: d.id,
          ruleId: "builtin-integer",
          message: `${d.name} must be a whole number.`,
        });
      }
      if (d.range?.min !== undefined && n < Number(d.range.min)) {
        errors.push({
          parameterId: d.id,
          ruleId: "builtin-range-min",
          message: `${d.name} must be ≥ ${d.range.min}.`,
        });
      }
      if (d.range?.max !== undefined && n > Number(d.range.max)) {
        errors.push({
          parameterId: d.id,
          ruleId: "builtin-range-max",
          message: `${d.name} must be ≤ ${d.range.max}.`,
        });
      }
    }

    if ((d.dataType === "date" || d.dataType === "date-time") && toTime(value) === null) {
      errors.push({
        parameterId: d.id,
        ruleId: "builtin-date",
        message: `${d.name} must be a valid date.`,
      });
    }

    if (d.domain === "list" && d.allowableValues?.length) {
      const allowed = d.allowableValues.map((a) => String(a.value));
      if (!allowed.includes(String(value))) {
        errors.push({
          parameterId: d.id,
          ruleId: "builtin-allowed",
          message: `${d.name} must be one of the allowed values.`,
        });
      }
    }
  }
  return errors;
}

/** Evaluate a single admin-authored rule. Returns an error or null. */
function evaluateRule(
  rule: ValidationRule,
  values: Record<string, ParameterValue>,
): ValidationError | null {
  if (!rule.enabled) return null;
  const value = values[rule.parameterId];
  const fail = (): ValidationError => ({
    parameterId: rule.parameterId,
    ruleId: rule.id,
    message: rule.message,
  });

  switch (rule.type) {
    case "required":
      return isBlank(value) ? fail() : null;

    case "min": {
      const n = toNumber(value);
      const bound = toNumber(rule.value as ParameterValue);
      if (n !== null && bound !== null && n < bound) return fail();
      return null;
    }
    case "max": {
      const n = toNumber(value);
      const bound = toNumber(rule.value as ParameterValue);
      if (n !== null && bound !== null && n > bound) return fail();
      return null;
    }
    case "range": {
      const n = toNumber(value);
      const [lo, hi] = (rule.value as ParameterValue[]).map((v) => toNumber(v));
      if (n !== null && lo !== null && hi !== null && (n < lo || n > hi)) return fail();
      return null;
    }
    case "date-before": {
      // value must be before compareParameter's value (e.g. Start < End).
      const a = toTime(value);
      const b = toTime(values[rule.compareParameterId ?? ""]);
      if (a !== null && b !== null && a > b) return fail();
      return null;
    }
    case "date-after": {
      const a = toTime(value);
      const b = toTime(values[rule.compareParameterId ?? ""]);
      if (a !== null && b !== null && a < b) return fail();
      return null;
    }
    case "allowed-values": {
      const allowed = (rule.value as ParameterValue[]).map(String);
      if (!isBlank(value) && !allowed.includes(String(value))) return fail();
      return null;
    }
    case "regex": {
      if (isBlank(value)) return null;
      try {
        const re = new RegExp(String(rule.value));
        return re.test(String(value)) ? null : fail();
      } catch {
        return null; // malformed admin regex — don't block the user
      }
    }
    default:
      return null;
  }
}

export function validate(input: ValidationInput): ValidationResult {
  const errors: ValidationError[] = [
    ...validateTypes(input.descriptors, input.values),
  ];

  for (const rule of input.rules) {
    const err = evaluateRule(rule, input.values);
    if (err) errors.push(err);
  }

  const byParameter: Record<string, ValidationError[]> = {};
  for (const e of errors) {
    (byParameter[e.parameterId] ??= []).push(e);
  }

  return { valid: errors.length === 0, errors, byParameter };
}
